/**
 * useOnboardingCheck Hook
 *
 * Determines whether the current user needs to see the onboarding tutorial.
 * Logic:
 *   - onboarding_completed === true in user_metadata -> skip onboarding
 *   - isNewUser (no learning data) AND not completed -> show onboarding
 *   - existing user with learning data but no flag -> skip (backward compat)
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/services/supabase';
import { useEmptyState } from './useEmptyState';
import { useLanguageStore } from '@/stores/useLanguageStore';

interface OnboardingCheckResult {
  /** Whether the user needs to complete onboarding */
  needsOnboarding: boolean;
  /** Whether the check is still loading */
  isLoading: boolean;
}

export function useOnboardingCheck(): OnboardingCheckResult {
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null);
  const { targetLanguage } = useLanguageStore();
  const { isNewUser, isLoading: emptyStateLoading } = useEmptyState(targetLanguage);

  useEffect(() => {
    let cancelled = false;

    async function checkOnboardingStatus() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!cancelled && session?.user) {
          const completed = session.user.user_metadata?.onboarding_completed === true;
          setOnboardingCompleted(completed);
        }
      } catch {
        // On error, don't block the user -- treat as completed
        if (!cancelled) setOnboardingCompleted(true);
      }
    }

    checkOnboardingStatus();
    return () => { cancelled = true; };
  }, []);

  // Still loading user_metadata or empty state data
  if (onboardingCompleted === null || emptyStateLoading) {
    return { needsOnboarding: false, isLoading: true };
  }

  // Already completed onboarding
  if (onboardingCompleted) {
    return { needsOnboarding: false, isLoading: false };
  }

  // Not completed: only redirect if truly a new user (backward compat for existing users)
  return { needsOnboarding: isNewUser, isLoading: false };
}
